import { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { useWorkspace } from '@/contexts/WorkspaceContext';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from '@/components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from 'sonner';
import {
  Video, Mic, MicOff, Square, Play, Phone, MapPin,
  Loader2, CheckCircle, XCircle, Brain, Send, User,
  Building2, Mail, Calendar, Clock, ChevronRight,
  FileText, Euro, Sparkles, Eye, Target, Zap, Shield,
  ArrowRight, Copy, ExternalLink, Download, PlusCircle,
  BarChart3, TrendingUp, Users, AlertTriangle, Handshake,
  Trash2, Pencil, Percent, MessageSquare, ListChecks,
  Rocket, CircleDot, CheckCircle2, HelpCircle,
  Activity, RefreshCw,
} from 'lucide-react';
import api from '@/lib/api';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// ============================
// SCRIPT DE VENTE (Questions programmees)
// ============================
const SALES_SCRIPT = [
  {
    phase: 'Ouverture',
    color: 'emerald',
    questions: [
      { id: 'q1', text: "Comment avez-vous connu Bureau Agricole ?", tip: "Identifier le canal d'acquisition" },
      { id: 'q2', text: "Quelle est votre activite principale ?", tip: "Ecouter attentivement pour detecter les modules" },
      { id: 'q3', text: "Depuis combien de temps etes-vous installe ?", tip: "Anciennete = stabilite = bon prospect" },
    ]
  },
  {
    phase: 'Qualification',
    color: 'blue',
    questions: [
      { id: 'q4', text: "Combien d'hectares exploitez-vous ?", tip: "Permet de calibrer le devis" },
      { id: 'q5', text: "Combien de salaries avez-vous ?", tip: "Si >2 salaries, proposer module RH" },
      { id: 'q6', text: "Quels outils utilisez-vous aujourd'hui ? (compta, gestion...)", tip: "Identifier les concurrents et les frustrations" },
      { id: 'q7', text: "Etes-vous en bio ou en conversion ?", tip: "Bio = tracabilite obligatoire = +1 module" },
    ]
  },
  {
    phase: 'Douleurs',
    color: 'red',
    questions: [
      { id: 'q8', text: "Quel est votre plus gros casse-tete administratif ?", tip: "LA question cle ! Noter exactement sa reponse" },
      { id: 'q9', text: "Combien de temps passez-vous sur la compta par semaine ?", tip: "Si >4h/sem = argument massue" },
      { id: 'q10', text: "Avez-vous deja perdu de l'argent a cause d'un oubli administratif ?", tip: "Creer l'urgence avec du concret" },
    ]
  },
  {
    phase: 'Solution',
    color: 'purple',
    questions: [
      { id: 'q11', text: "Laissez-moi vous montrer comment Bureau Agricole peut regler ca...", tip: "TRANSITION : passer a la demo live" },
      { id: 'q12', text: "Qu'est-ce qui vous plait le plus dans ce que je vous montre ?", tip: "Identifier le signal d'achat" },
      { id: 'q13', text: "Voyez-vous d'autres besoins qu'on pourrait couvrir ?", tip: "Cross-sell : modules additionnels" },
    ]
  },
  {
    phase: 'Closing',
    color: 'amber',
    questions: [
      { id: 'q14', text: "Quel budget avez-vous en tete pour votre gestion ?", tip: "Ne pas avoir peur de parler argent" },
      { id: 'q15', text: "Qui d'autre participe a la decision ?", tip: "Si conjoint/associe : proposer un 2e RDV" },
      { id: 'q16', text: "Souhaitez-vous tester gratuitement pendant 3 jours ?", tip: "Acces demo = engagement fort" },
      { id: 'q17', text: "Quand souhaitez-vous demarrer ?", tip: "Forcer la projection dans le futur" },
    ]
  },
];

// ============================
// MODULES & TARIFS
// ============================
const PACK_BASE_PRIX = 99; // EUR/mois
const MODULES_DISPO = [
  { id: 'exploitation', label: 'Gestion Exploitation', base: true, prix: 0, icon: Target, desc: 'Parcelles, cultures, planning', remplace: 'MesParcelles, Geofolia' },
  { id: 'dashboard', label: 'Dashboard & Reporting', base: true, prix: 0, icon: BarChart3, desc: 'Tableaux de bord, KPIs, alertes', remplace: 'Excel, tableurs manuels' },
  { id: 'comptabilite', label: 'Comptabilite', base: true, prix: 0, icon: Euro, desc: 'Saisie, rapprochements, TVA, bilan', remplace: 'Sage, Ciel Compta, EBP' },
  { id: 'facturation', label: 'Facturation electronique', base: true, prix: 0, icon: FileText, desc: 'Devis, factures, relances auto', remplace: 'Chorus Pro, facturiers papier' },
  { id: 'crm', label: 'CRM & Marketing', base: false, prix: 59, icon: Users, desc: 'Clients, prospects, campagnes, emailing', remplace: 'Mailchimp + fichier Excel clients' },
  { id: 'rh', label: 'Ressources Humaines', base: false, prix: 69, icon: Users, desc: 'Paie, conges, contrats, planning equipe', remplace: 'Silae, PayFit, fiches papier' },
  { id: 'materiel', label: 'Gestion Materiel & Flotte', base: false, prix: 45, icon: Zap, desc: 'Entretien, couts, planification machines', remplace: 'Carnet d\'entretien, Excel' },
  { id: 'ecommerce', label: 'E-commerce & Vente directe', base: false, prix: 79, icon: TrendingUp, desc: 'Boutique en ligne, drive fermier, paniers', remplace: 'Shopify, WooCommerce' },
  { id: 'stocks', label: 'Gestion des Stocks', base: false, prix: 45, icon: Building2, desc: 'Entrees/sorties, lots, alertes seuil', remplace: 'Gestion manuelle, fiches stock' },
  { id: 'tracabilite', label: 'Tracabilite & Certifications', base: false, prix: 55, icon: Shield, desc: 'Bio, HVE, cahier de culture, audits', remplace: 'Cahier papier, logiciels certif' },
  { id: 'parcellaire', label: 'Parcellaire avance & PAC', base: false, prix: 39, icon: MapPin, desc: 'Cartographie, RPG, telepac, assolement', remplace: 'Telepac seul, ISIS' },
];

const OPTIONS_PRIX = {
  workspace_supp: 25,
  collaborateur_supp: 15,
  agent_ia_supp: 29,
  google_drive: 0,
  reunion_supp_heure: 149,
};

const ONBOARDING = {
  gratuit: '30min offertes',
  reunion_supp: 149, // EUR/h
};

const FACTURATION = {
  mensuel: { label: 'Mensuel', mois: 1 },
  annuel: { label: 'Annuel (-1 mois offert)', mois: 11 }, // 11 mois au lieu de 12
};

// ============================
// METIERS & SOUS-OPTIONS
// ============================
const METIERS = [
  { id: 'viticulture', label: 'Viticulture', icon: '🍇',
    sousOptions: [
      { id: 'cave_vin', label: 'Cave a vin / Vinification' },
      { id: 'vente_caveau', label: 'Vente directe au caveau' },
      { id: 'negoce_vin', label: 'Negoce de vin' },
    ],
    modulesAuto: ['stocks', 'tracabilite', 'ecommerce'],
    agentsAuto: ['agent_viti'],
  },
  { id: 'elevage_bovin', label: 'Elevage bovin', icon: '🐄',
    sousOptions: [
      { id: 'bovin_lait', label: 'Production laitiere' },
      { id: 'bovin_viande', label: 'Elevage viande / Broutards' },
      { id: 'bovin_mixte', label: 'Mixte lait + viande' },
    ],
    modulesAuto: ['stocks', 'tracabilite'],
    agentsAuto: ['agent_elevage'],
  },
  { id: 'elevage_ovin', label: 'Elevage ovin / caprin', icon: '🐑',
    sousOptions: [
      { id: 'ovin_viande', label: 'Viande (agneaux)' },
      { id: 'ovin_lait', label: 'Lait (fromage, yaourt)' },
      { id: 'ovin_laine', label: 'Laine' },
    ],
    modulesAuto: ['stocks', 'tracabilite'],
    agentsAuto: ['agent_elevage'],
  },
  { id: 'grandes_cultures', label: 'Grandes cultures', icon: '🌾',
    sousOptions: [
      { id: 'cereales', label: 'Cereales (ble, mais, orge)' },
      { id: 'oleagineux', label: 'Oleagineux (colza, tournesol)' },
      { id: 'proteagineux', label: 'Proteagineux (pois, feverole)' },
    ],
    modulesAuto: ['parcellaire', 'materiel'],
    agentsAuto: ['agent_cultures'],
  },
  { id: 'maraichage', label: 'Maraichage / Legumes', icon: '🥬',
    sousOptions: [
      { id: 'plein_champ', label: 'Plein champ' },
      { id: 'sous_serre', label: 'Sous serre / tunnel' },
      { id: 'paniers_amap', label: 'Paniers / AMAP' },
    ],
    modulesAuto: ['stocks', 'ecommerce', 'tracabilite'],
    agentsAuto: ['agent_maraichage'],
  },
  { id: 'arboriculture', label: 'Arboriculture / Vergers', icon: '🍎',
    sousOptions: [
      { id: 'fruits_table', label: 'Fruits de table' },
      { id: 'transformation', label: 'Transformation (jus, compotes)' },
      { id: 'cueillette', label: 'Cueillette a la ferme' },
    ],
    modulesAuto: ['stocks', 'tracabilite', 'ecommerce'],
    agentsAuto: ['agent_arbo'],
  },
  { id: 'decoupe_transfo', label: 'Decoupe / Transformation', icon: '🔪',
    sousOptions: [
      { id: 'atelier_decoupe', label: 'Atelier de decoupe' },
      { id: 'abattage', label: 'Abattage a la ferme' },
      { id: 'conserverie', label: 'Conserverie / Conditionnement' },
      { id: 'fromagerie', label: 'Fromagerie / Laiterie' },
    ],
    modulesAuto: ['stocks', 'tracabilite', 'rh'],
    agentsAuto: ['agent_transfo', 'agent_hygiene'],
  },
  { id: 'apiculture', label: 'Apiculture', icon: '🐝',
    sousOptions: [
      { id: 'ruches_prod', label: 'Production de miel' },
      { id: 'gelee_royale', label: 'Gelee royale / Pollen' },
      { id: 'elevage_reines', label: 'Elevage de reines' },
    ],
    modulesAuto: ['stocks', 'ecommerce'],
    agentsAuto: ['agent_apiculture'],
  },
  { id: 'bio_hve', label: 'Agriculture Bio / HVE', icon: '🌱',
    sousOptions: [
      { id: 'certif_bio', label: 'Certifie AB' },
      { id: 'conversion_bio', label: 'En conversion bio' },
      { id: 'hve', label: 'Haute Valeur Environnementale (HVE)' },
    ],
    modulesAuto: ['tracabilite'],
    agentsAuto: ['agent_bio'],
  },
  { id: 'eta', label: 'ETA (Travaux Agricoles)', icon: '🚜',
    sousOptions: [
      { id: 'eta_moisson', label: 'Moisson / Recolte' },
      { id: 'eta_travaux_sol', label: 'Travaux du sol / Semis' },
      { id: 'eta_epandage', label: 'Epandage / Pulverisation' },
      { id: 'eta_transport', label: 'Transport agricole' },
    ],
    modulesAuto: ['materiel', 'rh', 'crm'],
    agentsAuto: ['agent_eta'],
  },
  { id: 'cuma', label: 'CUMA (Cooperative materiel)', icon: '🤝',
    sousOptions: [
      { id: 'cuma_location', label: 'Location de materiel' },
      { id: 'cuma_planning', label: 'Planning intercalaire' },
      { id: 'cuma_maintenance', label: 'Maintenance collective' },
    ],
    modulesAuto: ['materiel', 'stocks'],
    agentsAuto: ['agent_cuma'],
  },
  { id: 'syndicat', label: 'Syndicat agricole', icon: '🏛️',
    sousOptions: [
      { id: 'syndicat_local', label: 'Syndicat local / Cantonal' },
      { id: 'syndicat_depart', label: 'Federation departementale' },
      { id: 'syndicat_chambre', label: 'Chambre d\'agriculture' },
    ],
    modulesAuto: ['crm', 'rh'],
    agentsAuto: ['agent_juridique'],
  },
  { id: 'bio_conseil', label: 'Bio (Conseil & Conversion)', icon: '🌿',
    sousOptions: [
      { id: 'bio_accompagnement', label: 'Accompagnement conversion' },
      { id: 'bio_audit_certif', label: 'Audit certification AB' },
      { id: 'bio_formation', label: 'Formation agriculture bio' },
    ],
    modulesAuto: ['tracabilite', 'crm'],
    agentsAuto: ['agent_bio', 'agent_audit'],
  },
  { id: 'aide_audit', label: 'Aide audit & Conformite', icon: '📋',
    sousOptions: [
      { id: 'audit_pac', label: 'Audit PAC / Telepac' },
      { id: 'audit_certif', label: 'Audit certifications (Bio, HVE, GlobalGAP)' },
      { id: 'audit_compta', label: 'Audit comptable & fiscal' },
    ],
    modulesAuto: ['tracabilite', 'parcellaire'],
    agentsAuto: ['agent_audit'],
  },
  { id: 'carbone', label: 'Carbone & Credits climat', icon: '🌍',
    sousOptions: [
      { id: 'diag_carbone', label: 'Diagnostic carbone exploitation' },
      { id: 'label_bas_carbone', label: 'Label Bas-Carbone' },
      { id: 'vente_credits', label: 'Vente de credits carbone' },
    ],
    modulesAuto: ['tracabilite', 'parcellaire'],
    agentsAuto: ['agent_carbone'],
  },
];

// ============================
// AGENTS IA SPECIALISES (vendables)
// ============================
const AGENTS_IA = [
  { id: 'agent_viti', label: 'Agent Viticulture', prix: 29, desc: 'Suivi parcellaire vigne, vinification, cahier de cave' },
  { id: 'agent_elevage', label: 'Agent Elevage', prix: 29, desc: 'Suivi troupeau, reproduction, sanitaire, alimentation' },
  { id: 'agent_cultures', label: 'Agent Grandes Cultures', prix: 29, desc: 'Assolement, intrants, rendements, PAC' },
  { id: 'agent_maraichage', label: 'Agent Maraichage', prix: 29, desc: 'Planning semis, rotations, paniers' },
  { id: 'agent_arbo', label: 'Agent Arboriculture', prix: 29, desc: 'Traitements verger, recoltes, calibrage' },
  { id: 'agent_transfo', label: 'Agent Transformation', prix: 29, desc: 'Recettes, lots, DLC, etiquetage' },
  { id: 'agent_hygiene', label: 'Agent Hygiene & HACCP', prix: 29, desc: 'Plan de maitrise sanitaire, controles, audits' },
  { id: 'agent_apiculture', label: 'Agent Apiculture', prix: 29, desc: 'Suivi ruchers, transhumance, recoltes' },
  { id: 'agent_bio', label: 'Agent Bio & Certif', prix: 29, desc: 'Conformite bio, cahier des charges, audits' },
  { id: 'agent_juridique', label: 'Agent Juridique', prix: 39, desc: 'Droit rural, baux, PAC, litiges, reglementation' },
  { id: 'agent_fiscal', label: 'Agent Fiscal & Compta', prix: 39, desc: 'Optimisation fiscale, TVA agricole, declarations' },
  { id: 'agent_commercial', label: 'Agent Commercial', prix: 29, desc: 'Prospection, relances, suivi clients, devis auto' },
  { id: 'agent_eta', label: 'Agent ETA & Prestation', prix: 29, desc: 'Planning chantiers, suivi heures, facturation prestation' },
  { id: 'agent_cuma', label: 'Agent CUMA & Cooperative', prix: 29, desc: 'Gestion tours de role, cout horaire, planning partage' },
  { id: 'agent_audit', label: 'Agent Audit & Conformite', prix: 29, desc: 'Check-lists audit, conformite PAC, preparation controles' },
  { id: 'agent_carbone', label: 'Agent Bilan Carbone', prix: 29, desc: 'Diagnostic GES, credits carbone, Label Bas-Carbone' },
];

const calcPrixTotal = (modules, nbWorkspaces, nbCollabs, agentsSelected) => {
  const prixModules = MODULES_DISPO.filter(m => modules.includes(m.id)).reduce((s, m) => s + m.prix, 0);
  const prixWorkspaces = Math.max(0, (nbWorkspaces || 1) - 1) * OPTIONS_PRIX.workspace_supp;
  const prixCollabs = Math.max(0, (nbCollabs || 2) - 2) * OPTIONS_PRIX.collaborateur_supp;
  const prixAgents = (agentsSelected || []).reduce((s, aid) => {
    const a = AGENTS_IA.find(x => x.id === aid);
    return s + (a ? a.prix : 0);
  }, 0);
  return PACK_BASE_PRIX + prixModules + prixWorkspaces + prixCollabs + prixAgents;
};

const calcPrixAnnuel = (prixMensuel) => prixMensuel * 11; // 1 mois offert

// ============================
// COMPOSANT PRINCIPAL
// ============================
export default function EspaceCommercialPage() {
  const { user, token } = useAuth();
  const { activeSociete } = useWorkspace();

  // Etat global
  const [phase, setPhase] = useState('dashboard'); // dashboard | visio | partenaires
  const [rdvList, setRdvList] = useState([]);
  const [currentRdv, setCurrentRdv] = useState(null);
  const [loading, setLoading] = useState(true);
  const [pipeline, setPipeline] = useState([]);

  // Partenaires
  const [partenaires, setPartenaires] = useState([]);
  const [showPartenaireDialog, setShowPartenaireDialog] = useState(false);
  const [editPartenaire, setEditPartenaire] = useState(null);
  const [partenaireForm, setPartenaireForm] = useState({ nom: '', entreprise: '', email: '', telephone: '', type_partenaire: 'apporteur', commission_pct: 10, duree_commission_mois: 12, notes: '' });
  const [commissionsRapport, setCommissionsRapport] = useState(null);
  const [loadingCommissions, setLoadingCommissions] = useState(false);

  // Visio
  const [isRecording, setIsRecording] = useState(false);
  const isRecordingRef = useRef(false); // Fix: ref pour le callback de recognition
  const [recordingTime, setRecordingTime] = useState(0);
  const [liveTranscript, setLiveTranscript] = useState('');
  const [coachingTips, setCoachingTips] = useState([]);
  const [streamRecordId, setStreamRecordId] = useState(null);
  const streamRef = useRef(null);
  const mediaRecorderRef = useRef(null);
  const recognitionRef = useRef(null);
  const coachingIntervalRef = useRef(null);
  const transcriptBufferRef = useRef('');
  const timerRef = useRef(null);

  // Audit prospect
  const [prospect, setProspect] = useState({
    nom: '', prenom: '', email: '', telephone: '',
    entreprise: '', activites: [], surface_ha: '',
    departement: '', code_ape: '', bio: false,
    metiers: [], sous_options: [],
    besoins_detectes: [], points_douleur: [],
    modules_suggeres: ['exploitation', 'dashboard', 'comptabilite', 'facturation'],
    agents_ia_selectionnes: [],
    nb_workspaces: 1, nb_collaborateurs: 2,
    agents_ia_base: 3, agents_ia_specialises: [],
    nb_agents_ia: 3, google_drive: true,
    canal_vente: '', notes_ia: '', score_chaleur: 0,
    facturation: 'mensuel', // mensuel | annuel
    code_parrainage: '', // code du parrain si existe
  });

  // Closing (intgere dans la visio)
  const [devisGenere, setDevisGenere] = useState(null);
  const [accesDemo, setAccesDemo] = useState(null);
  const [documentsRequis, setDocumentsRequis] = useState([]);
  const [showOnboardingDialog, setShowOnboardingDialog] = useState(false);
  const [compteRendu, setCompteRendu] = useState(null);
  const [loadingCR, setLoadingCR] = useState(false);

  // Script de vente
  const [answeredQuestions, setAnsweredQuestions] = useState({});
  const [feedbackSent, setFeedbackSent] = useState({});

  // Suivi post-RDV
  const [suiviData, setSuiviData] = useState(null);
  const [loadingSuivi, setLoadingSuivi] = useState(false);

  const headers = { Authorization: `Bearer ${token}` };

  // ============================
  // CHARGEMENT
  // ============================
  const loadDashboard = useCallback(async () => {
    setLoading(true);
    try {
      const [rdvRes, pipelineRes, partRes] = await Promise.all([
        api.get('/booking/rdv').catch(() => ({ data: { rdv: [] } })),
        api.get('/crm-vente-logiciel/prospects').catch(() => ({ data: { prospects: [] } })),
        api.get('/crm-vente-logiciel/partenaires').catch(() => ({ data: { partenaires: [] } })),
      ]);
      const rdvs = (rdvRes.data.rdv || []).filter(r => r.statut === 'confirme');
      rdvs.sort((a, b) => `${a.date}${a.heure}`.localeCompare(`${b.date}${b.heure}`));
      setRdvList(rdvs);
      setPipeline(pipelineRes.data.prospects || []);
      setPartenaires(partRes.data.partenaires || []);
    } catch (err) {
      console.error('Erreur chargement:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadDashboard(); }, [loadDashboard]);

  // ============================
  // VISIO - Demarrage
  // ============================
  const startVisio = async (rdv) => {
    setCurrentRdv(rdv);
    setPhase('visio');
    setAnsweredQuestions({});
    setDevisGenere(null);
    setAccesDemo(null);
    setDocumentsRequis([]);
    setCoachingTips([]);
    setLiveTranscript('');
    setRecordingTime(0);
    setProspect(p => ({
      ...p,
      nom: rdv?.nom || '',
      email: rdv?.email || '',
      telephone: rdv?.telephone || '',
      entreprise: rdv?.entreprise || '',
      canal_vente: rdv?.canal || 'visio',
    }));

    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;

      const res = await fetch(`${API_URL}/api/visio/stream/start`, {
        method: 'POST', headers: { ...headers, 'Content-Type': 'application/json' },
        body: JSON.stringify({ titre: `Visio ${rdv?.nom || 'Prospect'}` }),
      });
      const data = await res.json();
      if (!data.success) throw new Error(data.error);
      setStreamRecordId(data.record_id);

      const mediaRecorder = new MediaRecorder(stream, { mimeType: 'audio/webm;codecs=opus' });
      mediaRecorderRef.current = mediaRecorder;
      mediaRecorder.ondataavailable = async (e) => {
        if (e.data.size > 0 && data.record_id) {
          try {
            await fetch(`${API_URL}/api/visio/stream/${data.record_id}/chunk`, {
              method: 'POST',
              headers: { ...headers, 'Content-Type': 'application/octet-stream' },
              body: e.data,
            });
          } catch (err) { console.error('Chunk error:', err); }
        }
      };
      mediaRecorder.start(5000);
      setIsRecording(true);
      isRecordingRef.current = true;
      transcriptBufferRef.current = '';

      timerRef.current = setInterval(() => setRecordingTime(t => t + 1), 1000);
      startLiveTranscription(data.record_id);
      toast.success('Visio demarree — L\'IA vous accompagne');
    } catch (err) {
      console.warn('Micro non disponible, mode preparation:', err.message);
      toast.info('Mode preparation — sans microphone. Vous pouvez configurer le prospect.');
    }
  };

  const startLiveTranscription = (recordId) => {
    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SpeechRecognition) return;

    const recognition = new SpeechRecognition();
    recognition.continuous = true;
    recognition.interimResults = true;
    recognition.lang = 'fr-FR';
    recognitionRef.current = recognition;

    recognition.onresult = (event) => {
      let interim = '';
      let final = '';
      for (let i = event.resultIndex; i < event.results.length; i++) {
        const t = event.results[i][0].transcript;
        if (event.results[i].isFinal) {
          final += t + ' ';
        } else {
          interim += t;
        }
      }
      if (final) {
        transcriptBufferRef.current += final;
        setLiveTranscript(transcriptBufferRef.current.slice(-2000));
        extractProspectInfo(final);
      } else if (interim) {
        // Afficher aussi les resultats intermediaires pour le streaming visuel
        setLiveTranscript(transcriptBufferRef.current.slice(-1800) + ' ...' + interim);
      }
    };
    recognition.onerror = (e) => {
      if (e.error !== 'no-speech') console.warn('Speech error:', e.error);
    };
    recognition.onend = () => {
      // Fix: utiliser ref au lieu du state pour eviter le bug de closure
      if (isRecordingRef.current) {
        try { recognition.start(); } catch (e) {}
      }
    };
    recognition.start();

    // Guide proactif IA toutes les 15s
    coachingIntervalRef.current = setInterval(async () => {
      if (!transcriptBufferRef.current) return;
      try {
        const res = await fetch(`${API_URL}/api/visio/stream/${recordId}/coaching`, {
          method: 'POST',
          headers: { ...headers, 'Content-Type': 'application/json' },
          body: JSON.stringify({
            accumulated_transcript: transcriptBufferRef.current,
            prospect_metiers: prospect.metiers || [],
            prospect_modules: prospect.modules_suggeres || [],
          }),
        });
        const data = await res.json();
        if (data.tips?.length) setCoachingTips(prev => [...data.tips, ...prev].slice(0, 20));
      } catch (err) { console.error('Guide proactif error:', err); }
    }, 15000);
  };

  // IA extrait les infos du prospect
  const extractProspectInfo = (text) => {
    const lower = text.toLowerCase();
    const activites_keywords = {
      viticulture: ['vigne', 'vin', 'cave', 'vendange', 'cep'],
      elevage_bovin: ['vache', 'bovin', 'taureau', 'lait'],
      elevage_ovin: ['brebis', 'mouton', 'agneau', 'ovin'],
      grandes_cultures: ['ble', 'mais', 'colza', 'tournesol', 'cereale', 'hectare'],
      maraichage: ['legume', 'maraich', 'serre', 'tomate'],
      arboriculture: ['arbre', 'pomme', 'verger', 'poire', 'cerise'],
      bio: ['bio', 'biologique', 'agriculture biologique'],
    };
    for (const [key, words] of Object.entries(activites_keywords)) {
      if (words.some(w => lower.includes(w))) {
        setProspect(p => {
          if (!p.activites.includes(key)) return { ...p, activites: [...p.activites, key] };
          return p;
        });
      }
    }
    if (lower.includes('bio') || lower.includes('biologique')) {
      setProspect(p => ({ ...p, bio: true }));
    }
  };

  // ============================
  // ARRET VISIO + Sauvegarde CRM
  // ============================
  const stopVisio = async () => {
    setIsRecording(false);
    isRecordingRef.current = false;
    if (timerRef.current) clearInterval(timerRef.current);
    if (coachingIntervalRef.current) clearInterval(coachingIntervalRef.current);
    if (recognitionRef.current) { try { recognitionRef.current.stop(); } catch (e) {} }
    if (streamRef.current) streamRef.current.getTracks().forEach(t => t.stop());

    if (streamRecordId) {
      try {
        await fetch(`${API_URL}/api/visio/stream/${streamRecordId}/stop`, {
          method: 'POST', headers,
        });
      } catch (err) { console.error('Stop error:', err); }
    }

    try {
      await api.post('/crm-vente-logiciel/creer-prospect', {
        ...prospect,
        surface_ha: parseInt(prospect.surface_ha) || 0,
        statut: 'qualifie',
        source: 'visio_audit',
        notes: `Visio du ${new Date().toLocaleDateString('fr-FR')} — Transcript: ${transcriptBufferRef.current.slice(0, 500)}`,
        canal_acquisition: prospect.canal_vente,
      });
      toast.success('Prospect sauvegarde dans le CRM');
    } catch (err) { console.error('Erreur CRM:', err); }

    toast.success('Visio terminee — Prospect enregistre');
    // On reste sur la meme page, pas de changement de phase

    // Generer le compte-rendu post-visio automatiquement
    generateCompteRendu();
  };

  // ============================
  // COMPTE-RENDU POST-VISIO (IA)
  // ============================
  const generateCompteRendu = async () => {
    if (!transcriptBufferRef.current && coachingTips.length === 0) return;
    setLoadingCR(true);
    try {
      const res = await api.post('/visio/compte-rendu', {
        transcript: transcriptBufferRef.current || '',
        prospect_info: {
          nom: prospect.nom, prenom: prospect.prenom,
          entreprise: prospect.entreprise, metiers: prospect.metiers,
          modules: prospect.modules_suggeres, agents: prospect.agents_ia_selectionnes,
          surface_ha: prospect.surface_ha, facturation: prospect.facturation,
          code_parrainage: prospect.code_parrainage,
        },
        prix_mensuel: calcPrixTotal(prospect.modules_suggeres, prospect.nb_workspaces, prospect.nb_collaborateurs, prospect.agents_ia_selectionnes),
        coaching_tips_used: coachingTips.filter((_, i) => feedbackSent[i] === 'done').map(t => t.texte),
        coaching_tips_skipped: coachingTips.filter((_, i) => feedbackSent[i] === 'skipped').map(t => t.texte),
        devis_envoye: !!devisGenere,
        demo_creee: !!accesDemo,
      });
      if (res.data.success) {
        setCompteRendu(res.data.compte_rendu);
        toast.success('Compte-rendu post-visio genere');
      }
    } catch (err) { console.error('Erreur compte-rendu:', err); }
    finally { setLoadingCR(false); }
  };

  // ============================
  // CLOSING - Actions (disponibles PENDANT la visio)
  // ============================
  const genererDevis = async () => {
    const prixMensuel = calcPrixTotal(prospect.modules_suggeres, prospect.nb_workspaces, prospect.nb_collaborateurs, prospect.agents_ia_selectionnes);
    try {
      const res = await api.post('/crm-vente-logiciel/generer-devis', {
        prospect: { ...prospect, prix_mensuel_propose: prixMensuel },
        modules: prospect.modules_suggeres,
        agents_specialises: prospect.agents_ia_specialises,
        prix_mensuel: prixMensuel,
        nb_workspaces: prospect.nb_workspaces,
        nb_collaborateurs: prospect.nb_collaborateurs,
        nb_agents_ia: prospect.nb_agents_ia,
      });
      if (res.data.success) { setDevisGenere({ ...res.data.devis, montant_mensuel: prixMensuel }); toast.success(`Devis genere : ${prixMensuel} EUR/mois`); }
    } catch (err) { toast.error('Erreur generation devis'); }
  };

  const genererAccesDemo = async () => {
    try {
      const res = await api.post('/crm-vente-logiciel/generer-acces-demo', {
        prospect, duree_jours: 3,
      });
      if (res.data.success) {
        setAccesDemo(res.data.acces);
        setDocumentsRequis(getDocumentsRequis(prospect.modules_suggeres));
        toast.success('Acces demo cree (3 jours)');
      }
    } catch (err) { toast.error('Erreur creation acces demo'); }
  };

  const getDocumentsRequis = (modules) => {
    const docs = [
      { id: 'bilan', label: 'Dernier bilan comptable', requis: true },
      { id: 'kbis', label: 'Extrait Kbis / MSA', requis: true },
      { id: 'google', label: 'Acces Google (Gmail, Drive, Calendar)', requis: true },
    ];
    if (modules.includes('comptabilite')) docs.push({ id: 'comptable', label: 'Coordonnees cabinet comptable', requis: true });
    if (modules.includes('facturation')) docs.push({ id: 'factures', label: 'Modele de factures actuel', requis: false });
    if (modules.includes('rh')) docs.push({ id: 'salaries', label: 'Liste des salaries + contrats', requis: true });
    if (modules.includes('exploitation')) docs.push({ id: 'parcellaire', label: 'Plan parcellaire (PAC/RPG)', requis: false });
    if (prospect.bio) docs.push({ id: 'certif_bio', label: 'Certificat Bio', requis: true });
    return docs;
  };

  const envoyerRapportPartenaire = async (partenaireId) => {
    try {
      await api.post(`/crm-vente-logiciel/partenaires/${partenaireId}/envoyer-rapport`, {
        prospect, score_chaleur: prospect.score_chaleur || 0,
        notes_rdv: prospect.notes_ia || '',
        modules_suggeres: prospect.modules_suggeres,
        statut_pipeline: 'qualifie',
      });
      toast.success('Rapport envoye au partenaire');
    } catch (err) { toast.error('Erreur envoi rapport'); }
  };

  // Feedback guide proactif (marquer comme fait / note)
  const markTipAction = async (tipIndex, action) => {
    const tip = coachingTips[tipIndex];
    if (!tip) return;
    try {
      await api.post('/visio/coaching/feedback', {
        tip_type: tip.type || 'general',
        was_useful: action === 'done',
        action,
        note: action === 'done' ? 'Conseil applique' : 'Conseil ignore',
        context: transcriptBufferRef.current?.slice(-200) || '',
      });
      setFeedbackSent(prev => ({ ...prev, [tipIndex]: action }));
    } catch (err) { console.error('Feedback error:', err); }
  };

  // Suivi post-RDV
  const loadSuivi = async () => {
    setLoadingSuivi(true);
    try {
      const res = await api.get('/crm-vente-logiciel/suivi-post-rdv');
      setSuiviData(res.data);
    } catch (err) { toast.error('Erreur chargement suivi'); }
    finally { setLoadingSuivi(false); }
  };

  // PARTENAIRES CRUD
  const savePartenaire = async () => {
    try {
      if (editPartenaire) {
        await api.put(`/crm-vente-logiciel/partenaires/${editPartenaire.id}`, partenaireForm);
        toast.success('Partenaire modifie');
      } else {
        await api.post('/crm-vente-logiciel/partenaires', partenaireForm);
        toast.success('Partenaire cree');
      }
      setShowPartenaireDialog(false);
      setEditPartenaire(null);
      setPartenaireForm({ nom: '', entreprise: '', email: '', telephone: '', type_partenaire: 'apporteur', commission_pct: 10, duree_commission_mois: 12, notes: '' });
      loadDashboard();
    } catch (err) { toast.error(err.response?.data?.detail || 'Erreur'); }
  };
  const deletePartenaire = async (id) => {
    if (!window.confirm('Supprimer ce partenaire ?')) return;
    try { await api.delete(`/crm-vente-logiciel/partenaires/${id}`); toast.success('Supprime'); loadDashboard(); } catch (err) { toast.error('Erreur'); }
  };
  const openEditPartenaire = (p) => {
    setEditPartenaire(p);
    setPartenaireForm({ nom: p.nom, entreprise: p.entreprise, email: p.email, telephone: p.telephone, type_partenaire: p.type_partenaire, commission_pct: p.commission_pct, duree_commission_mois: p.duree_commission_mois, notes: p.notes });
    setShowPartenaireDialog(true);
  };
  const loadCommissions = async () => {
    setLoadingCommissions(true);
    try { const res = await api.get('/crm-vente-logiciel/partenaires/commissions'); setCommissionsRapport(res.data); }
    catch (err) { toast.error('Erreur commissions'); }
    finally { setLoadingCommissions(false); }
  };

  const formatTime = (s) => `${String(Math.floor(s / 60)).padStart(2, '0')}:${String(s % 60).padStart(2, '0')}`;

  // Quand Elsa toggle un metier, auto-coche les modules et agents associes
  const toggleMetier = (metierId) => {
    setProspect(p => {
      const wasSelected = p.metiers.includes(metierId);
      const newMetiers = wasSelected ? p.metiers.filter(m => m !== metierId) : [...p.metiers, metierId];
      if (wasSelected) return { ...p, metiers: newMetiers };
      // Auto-ajouter modules et agents du metier
      const metier = METIERS.find(m => m.id === metierId);
      if (!metier) return { ...p, metiers: newMetiers };
      const newModules = [...new Set([...p.modules_suggeres, ...(metier.modulesAuto || [])])];
      const newAgents = [...new Set([...p.agents_ia_selectionnes, ...(metier.agentsAuto || [])])];
      return { ...p, metiers: newMetiers, modules_suggeres: newModules, agents_ia_selectionnes: newAgents };
    });
  };

  const toggleSousOption = (optionId) => {
    setProspect(p => ({
      ...p, sous_options: p.sous_options.includes(optionId)
        ? p.sous_options.filter(o => o !== optionId)
        : [...p.sous_options, optionId]
    }));
  };

  const toggleAgentIA = (agentId) => {
    setProspect(p => ({
      ...p, agents_ia_selectionnes: p.agents_ia_selectionnes.includes(agentId)
        ? p.agents_ia_selectionnes.filter(a => a !== agentId)
        : [...p.agents_ia_selectionnes, agentId]
    }));
  };

  const todayStr = new Date().toISOString().split('T')[0];
  const todayRdvs = rdvList.filter(r => r.date === todayStr);
  const nextRdv = rdvList.find(r => r.date >= todayStr);
  const hotProspects = pipeline.filter(p => (p.score_chaleur || 0) >= 7);
  const selectedPartenaire = partenaires.find(p => p.id === prospect.canal_vente);
  const questionsAnswered = Object.values(answeredQuestions).filter(Boolean).length;
  const totalQuestions = SALES_SCRIPT.reduce((acc, s) => acc + s.questions.length, 0);

  if (loading) return (
    <div className="flex items-center justify-center h-[60vh]" data-testid="loading-commercial">
      <Loader2 className="w-8 h-8 animate-spin text-emerald-600" />
    </div>
  );

  return (
    <div className="space-y-4 relative" data-testid="espace-commercial">
      {/* ===== DASHBOARD ===== */}
      {phase === 'dashboard' && (
        <DashboardView
          todayRdvs={todayRdvs} nextRdv={nextRdv} rdvList={rdvList}
          pipeline={pipeline} hotProspects={hotProspects} partenaires={partenaires}
          suiviData={suiviData} loadingSuivi={loadingSuivi}
          onStartVisio={startVisio} onRefresh={loadDashboard}
          onGoPartenaires={() => { setPhase('partenaires'); loadCommissions(); }}
          onLoadSuivi={loadSuivi}
        />
      )}

      {/* ===== VISIO (Tunnel de vente complet — une seule page) ===== */}
      {phase === 'visio' && (
        <div className="grid grid-cols-12 gap-3 h-[calc(100vh-120px)]" data-testid="visio-tunnel-view">
          {/* COL GAUCHE (8/12) : Barre visio + Audit prospect */}
          <div className="col-span-8 space-y-3 flex flex-col min-h-0">
            {/* Barre visio */}
            <Card className={`border-2 shrink-0 ${isRecording ? 'border-red-400 bg-red-50/50' : 'border-emerald-400 bg-emerald-50/30'}`}>
              <CardContent className="p-3 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  {isRecording && <div className="w-3 h-3 bg-red-500 rounded-full animate-pulse" />}
                  {!isRecording && recordingTime > 0 && <CheckCircle className="w-4 h-4 text-emerald-600" />}
                  <span className="font-semibold text-sm">{currentRdv?.nom || 'Visio'}</span>
                  <Badge variant="outline" className="capitalize text-xs">{currentRdv?.canal || 'visio'}</Badge>
                  <span className="font-mono text-sm">{formatTime(recordingTime)}</span>
                  {questionsAnswered > 0 && (
                    <Badge className="bg-blue-100 text-blue-700 text-xs">{questionsAnswered}/{totalQuestions} questions</Badge>
                  )}
                  <Badge className="bg-emerald-100 text-emerald-800 text-xs font-bold" data-testid="visio-prix-badge">
                    {prospect.facturation === 'annuel'
                      ? `${calcPrixAnnuel(calcPrixTotal(prospect.modules_suggeres, prospect.nb_workspaces, prospect.nb_collaborateurs, prospect.agents_ia_selectionnes))} EUR/an`
                      : `${calcPrixTotal(prospect.modules_suggeres, prospect.nb_workspaces, prospect.nb_collaborateurs, prospect.agents_ia_selectionnes)} EUR/mois`
                    }
                  </Badge>
                </div>
                <div className="flex items-center gap-2">
                  <Button
                    variant="outline" size="sm"
                    className="border-amber-300 text-amber-700 hover:bg-amber-50 text-xs"
                    onClick={() => {
                      const meetUrl = `https://meet.google.com/new`;
                      window.open(meetUrl, '_blank');
                      toast.success('Lien Google Meet ouvert — connectez-vous avec le compte ETA Sauvayre');
                    }}
                    data-testid="btn-invite-eta-sauvayre"
                  >
                    <Phone className="w-3.5 h-3.5 mr-1" />Inviter ETA Sauvayre
                  </Button>
                  {isRecording ? (
                    <Button variant="destructive" size="sm" className="text-xs" onClick={stopVisio} data-testid="btn-stop-visio">
                      <Square className="w-3.5 h-3.5 mr-1" />Arreter l'enregistrement
                    </Button>
                  ) : recordingTime > 0 ? (
                    <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700 text-xs" onClick={() => { setPhase('dashboard'); loadDashboard(); }} data-testid="btn-back-dashboard">
                      <ArrowRight className="w-3.5 h-3.5 mr-1" />Retour dashboard
                    </Button>
                  ) : null}
                </div>
              </CardContent>
            </Card>

            {/* Fiche prospect + Transcription */}
            <Card className="flex-1 overflow-auto min-h-0">
              <Tabs defaultValue="prospect" className="h-full flex flex-col">
                <TabsList className="mx-4 mt-3 shrink-0">
                  <TabsTrigger value="prospect" className="text-xs"><User className="w-3.5 h-3.5 mr-1" />Fiche prospect</TabsTrigger>
                  <TabsTrigger value="transcript" className="text-xs"><MessageSquare className="w-3.5 h-3.5 mr-1" />Transcription{liveTranscript ? ' (live)' : ''}</TabsTrigger>
                  <TabsTrigger value="closing" className="text-xs"><Rocket className="w-3.5 h-3.5 mr-1" />Actions closing</TabsTrigger>
                </TabsList>

                <TabsContent value="prospect" className="flex-1 overflow-auto px-4 pb-4 mt-2">
                  <div className="space-y-3">
                    {/* Infos contact */}
                    <div className="grid grid-cols-2 gap-2">
                      <Input placeholder="Nom" value={prospect.nom} onChange={e => setProspect(p => ({ ...p, nom: e.target.value }))} data-testid="audit-nom" />
                      <Input placeholder="Prenom" value={prospect.prenom} onChange={e => setProspect(p => ({ ...p, prenom: e.target.value }))} data-testid="audit-prenom" />
                      <Input placeholder="Email" value={prospect.email} onChange={e => setProspect(p => ({ ...p, email: e.target.value }))} data-testid="audit-email" />
                      <Input placeholder="Telephone" value={prospect.telephone} onChange={e => setProspect(p => ({ ...p, telephone: e.target.value }))} data-testid="audit-telephone" />
                      <Input placeholder="Entreprise" value={prospect.entreprise} onChange={e => setProspect(p => ({ ...p, entreprise: e.target.value }))} data-testid="audit-entreprise" />
                      <Input placeholder="Surface (ha)" value={prospect.surface_ha} onChange={e => setProspect(p => ({ ...p, surface_ha: e.target.value }))} />
                    </div>

                    {partenaires.length > 0 && (
                      <Select value={prospect.canal_vente || 'direct'} onValueChange={v => setProspect(p => ({ ...p, canal_vente: v }))}>
                        <SelectTrigger className="text-xs" data-testid="audit-canal-vente"><SelectValue placeholder="Canal de vente" /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="direct">Direct (pas de partenaire)</SelectItem>
                          {partenaires.filter(p => p.actif !== false).map(p => (
                            <SelectItem key={p.id} value={p.id}>{p.nom} — {p.type_partenaire} ({p.commission_pct}%)</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    )}

                    {/* METIERS — Choix de l'activite */}
                    <div>
                      <p className="text-xs font-bold uppercase tracking-wide text-amber-700 mb-1.5">Type d'exploitation</p>
                      <div className="grid grid-cols-3 gap-1.5">
                        {METIERS.map(m => {
                          const sel = prospect.metiers.includes(m.id);
                          return (
                            <div key={m.id}
                              className={`p-2 rounded-lg cursor-pointer transition-all border text-xs text-center ${sel ? 'bg-amber-50 border-amber-400 font-semibold' : 'bg-white border-gray-100 hover:border-amber-200'}`}
                              onClick={() => toggleMetier(m.id)}
                              data-testid={`metier-toggle-${m.id}`}
                            >
                              <span className="text-base">{m.icon}</span>
                              <p className={sel ? 'text-amber-800' : 'text-muted-foreground'}>{m.label}</p>
                            </div>
                          );
                        })}
                      </div>
                    </div>

                    {/* Sous-options des metiers selectionnes */}
                    {prospect.metiers.length > 0 && (
                      <div className="bg-amber-50/50 rounded-lg p-2.5 space-y-2">
                        {prospect.metiers.map(mid => {
                          const metier = METIERS.find(m => m.id === mid);
                          if (!metier) return null;
                          return (
                            <div key={mid}>
                              <p className="text-xs font-semibold text-amber-800 mb-1">{metier.icon} {metier.label} — Precisions :</p>
                              <div className="flex flex-wrap gap-1.5">
                                {metier.sousOptions.map(so => {
                                  const sel = prospect.sous_options.includes(so.id);
                                  return (
                                    <Badge key={so.id}
                                      className={`cursor-pointer transition-all text-xs ${sel ? 'bg-amber-200 text-amber-900 hover:bg-amber-300' : 'bg-white border border-amber-200 text-amber-700 hover:bg-amber-50'}`}
                                      onClick={() => toggleSousOption(so.id)}
                                      data-testid={`sous-option-${so.id}`}
                                    >{sel ? '✓ ' : ''}{so.label}</Badge>
                                  );
                                })}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}

                    {/* Prix total en temps reel */}
                    <div className="bg-gradient-to-r from-emerald-600 to-emerald-700 text-white rounded-lg p-3" data-testid="prix-total-panel">
                      <div className="flex items-center justify-between mb-2">
                        <div>
                          <p className="text-xs opacity-80">Abonnement {prospect.facturation === 'annuel' ? 'annuel' : 'mensuel'}</p>
                          {prospect.facturation === 'annuel' ? (
                            <div>
                              <p className="text-2xl font-bold" data-testid="prix-total">{calcPrixAnnuel(calcPrixTotal(prospect.modules_suggeres, prospect.nb_workspaces, prospect.nb_collaborateurs, prospect.agents_ia_selectionnes))} EUR<span className="text-sm font-normal opacity-80">/an</span></p>
                              <p className="text-[11px] opacity-80">soit {calcPrixTotal(prospect.modules_suggeres, prospect.nb_workspaces, prospect.nb_collaborateurs, prospect.agents_ia_selectionnes)} EUR/mois — <span className="font-semibold text-emerald-200">1 mois OFFERT</span></p>
                            </div>
                          ) : (
                            <p className="text-2xl font-bold" data-testid="prix-total">{calcPrixTotal(prospect.modules_suggeres, prospect.nb_workspaces, prospect.nb_collaborateurs, prospect.agents_ia_selectionnes)} EUR<span className="text-sm font-normal opacity-80">/mois</span></p>
                          )}
                        </div>
                        <div className="text-right text-xs opacity-80">
                          <p>Base : {PACK_BASE_PRIX} EUR</p>
                          <p>+ {MODULES_DISPO.filter(m => prospect.modules_suggeres.includes(m.id) && !m.base).length} modules + {prospect.agents_ia_selectionnes.length} agent(s) IA</p>
                          <p>Remplace {MODULES_DISPO.filter(m => prospect.modules_suggeres.includes(m.id) && m.remplace).length}+ logiciels</p>
                        </div>
                      </div>
                      {/* Toggle mensuel / annuel */}
                      <div className="flex items-center gap-2 bg-emerald-800/40 rounded-lg p-1.5" data-testid="facturation-toggle">
                        <button
                          className={`flex-1 text-[11px] py-1 rounded font-medium transition-all ${prospect.facturation === 'mensuel' ? 'bg-white text-emerald-700' : 'text-emerald-200 hover:text-white'}`}
                          onClick={() => setProspect(p => ({ ...p, facturation: 'mensuel' }))}
                          data-testid="btn-mensuel"
                        >Mensuel</button>
                        <button
                          className={`flex-1 text-[11px] py-1 rounded font-medium transition-all ${prospect.facturation === 'annuel' ? 'bg-white text-emerald-700' : 'text-emerald-200 hover:text-white'}`}
                          onClick={() => setProspect(p => ({ ...p, facturation: 'annuel' }))}
                          data-testid="btn-annuel"
                        >Annuel <span className="text-[9px] opacity-80">(-1 mois)</span></button>
                      </div>
                    </div>

                    {/* Modules de base */}
                    <div>
                      <p className="text-xs font-semibold text-emerald-700 mb-1 flex items-center gap-1"><CheckCircle className="w-3.5 h-3.5" />Pack de base ({PACK_BASE_PRIX} EUR/mois)</p>
                      <div className="space-y-0.5">
                        {MODULES_DISPO.filter(m => m.base).map(m => (
                          <div key={m.id} className="flex items-center gap-2 text-xs p-1 rounded bg-emerald-50 text-emerald-700">
                            <CheckCircle2 className="w-3 h-3 shrink-0" />
                            <span className="font-medium">{m.label}</span>
                            <span className="text-emerald-600/60 text-[10px]">— {m.desc}</span>
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Modules payants */}
                    <div>
                      <p className="text-xs font-semibold text-blue-700 mb-1 flex items-center gap-1"><PlusCircle className="w-3.5 h-3.5" />Modules additionnels</p>
                      <div className="space-y-1">
                        {MODULES_DISPO.filter(m => !m.base).map(m => {
                          const selected = prospect.modules_suggeres.includes(m.id);
                          return (
                            <div key={m.id}
                              className={`flex items-center gap-2 text-xs p-1.5 rounded-lg cursor-pointer transition-all border ${selected ? 'bg-blue-50 border-blue-300 text-blue-800' : 'bg-white border-gray-100 text-muted-foreground hover:border-blue-200'}`}
                              onClick={() => setProspect(p => ({ ...p, modules_suggeres: selected ? p.modules_suggeres.filter(x => x !== m.id) : [...p.modules_suggeres, m.id] }))}
                              data-testid={`module-toggle-${m.id}`}
                            >
                              {selected ? <CheckCircle2 className="w-3.5 h-3.5 text-blue-600 shrink-0" /> : <CircleDot className="w-3.5 h-3.5 shrink-0 opacity-30" />}
                              <div className="min-w-0 flex-1">
                                <span className="font-medium">{m.label}</span>
                                <span className="opacity-60 ml-1 text-[10px]">{m.desc}</span>
                                {m.remplace && <span className="block text-[10px] italic opacity-40">Remplace : {m.remplace}</span>}
                              </div>
                              <span className={`font-bold shrink-0 ${selected ? 'text-blue-700' : ''}`}>+{m.prix}</span>
                            </div>
                          );
                        })}
                      </div>
                    </div>

                    {/* Agents IA specialises */}
                    <div>
                      <p className="text-xs font-bold uppercase tracking-wide text-purple-700 mb-1.5 flex items-center gap-1"><Brain className="w-3.5 h-3.5" />Agents IA specialises</p>
                      <p className="text-[10px] text-muted-foreground mb-1.5">Des agents intelligents dedies a votre metier, disponibles 24h/24</p>
                      <div className="space-y-1">
                        {AGENTS_IA.map(a => {
                          const sel = prospect.agents_ia_selectionnes.includes(a.id);
                          const autoSuggested = prospect.metiers.some(mid => METIERS.find(m => m.id === mid)?.agentsAuto?.includes(a.id));
                          return (
                            <div key={a.id}
                              className={`flex items-center gap-2 text-xs p-1.5 rounded-lg cursor-pointer transition-all border ${sel ? 'bg-purple-50 border-purple-300 text-purple-800' : 'bg-white border-gray-100 text-muted-foreground hover:border-purple-200'}`}
                              onClick={() => toggleAgentIA(a.id)}
                              data-testid={`agent-toggle-${a.id}`}
                            >
                              {sel ? <CheckCircle2 className="w-3.5 h-3.5 text-purple-600 shrink-0" /> : <CircleDot className="w-3.5 h-3.5 shrink-0 opacity-30" />}
                              <div className="min-w-0 flex-1">
                                <span className="font-medium">{a.label}</span>
                                {autoSuggested && !sel && <Badge className="ml-1 text-[9px] bg-amber-100 text-amber-700">Recommande</Badge>}
                                <span className="opacity-60 ml-1 text-[10px]">{a.desc}</span>
                              </div>
                              <span className={`font-bold shrink-0 ${sel ? 'text-purple-700' : ''}`}>+{a.prix}</span>
                            </div>
                          );
                        })}
                      </div>
                    </div>

                    {/* Options */}
                    <div className="space-y-2 bg-gray-50 rounded-lg p-2.5">
                      <p className="text-xs font-semibold text-muted-foreground flex items-center gap-1"><Zap className="w-3.5 h-3.5" />Options</p>
                      <div className="flex items-center justify-between text-xs">
                        <div className="flex items-center gap-2"><Building2 className="w-3.5 h-3.5" /><span>Workspaces</span><span className="text-[10px] text-muted-foreground">(1 inclus, +{OPTIONS_PRIX.workspace_supp}/supp.)</span></div>
                        <div className="flex items-center gap-1">
                          <Button variant="outline" size="icon" className="h-6 w-6 text-xs" onClick={() => setProspect(p => ({ ...p, nb_workspaces: Math.max(1, (p.nb_workspaces || 1) - 1) }))} data-testid="btn-workspace-minus">-</Button>
                          <span className="w-6 text-center font-bold" data-testid="nb-workspaces">{prospect.nb_workspaces || 1}</span>
                          <Button variant="outline" size="icon" className="h-6 w-6 text-xs" onClick={() => setProspect(p => ({ ...p, nb_workspaces: (p.nb_workspaces || 1) + 1 }))} data-testid="btn-workspace-plus">+</Button>
                        </div>
                      </div>
                      <div className="flex items-center justify-between text-xs">
                        <div className="flex items-center gap-2"><Users className="w-3.5 h-3.5" /><span>Collaborateurs</span><span className="text-[10px] text-muted-foreground">(2 inclus, +{OPTIONS_PRIX.collaborateur_supp}/supp.)</span></div>
                        <div className="flex items-center gap-1">
                          <Button variant="outline" size="icon" className="h-6 w-6 text-xs" onClick={() => setProspect(p => ({ ...p, nb_collaborateurs: Math.max(1, (p.nb_collaborateurs || 2) - 1) }))} data-testid="btn-collab-minus">-</Button>
                          <span className="w-6 text-center font-bold" data-testid="nb-collaborateurs">{prospect.nb_collaborateurs || 2}</span>
                          <Button variant="outline" size="icon" className="h-6 w-6 text-xs" onClick={() => setProspect(p => ({ ...p, nb_collaborateurs: (p.nb_collaborateurs || 2) + 1 }))} data-testid="btn-collab-plus">+</Button>
                        </div>
                      </div>
                      <div className="flex items-center justify-between text-xs">
                        <div className="flex items-center gap-2"><ExternalLink className="w-3.5 h-3.5 text-emerald-600" /><span>Connexion Google Drive</span></div>
                        <Badge className="bg-emerald-100 text-emerald-700 text-[10px]">Inclus</Badge>
                      </div>
                    </div>

                    {/* Onboarding & Accompagnement */}
                    <div className="space-y-2 bg-blue-50/50 rounded-lg p-2.5 border border-blue-100" data-testid="onboarding-section">
                      <p className="text-xs font-semibold text-blue-700 flex items-center gap-1"><Calendar className="w-3.5 h-3.5" />Onboarding & Accompagnement</p>
                      <div className="flex items-center justify-between text-xs">
                        <div className="flex items-center gap-2">
                          <CheckCircle className="w-3.5 h-3.5 text-emerald-600" />
                          <span className="font-medium">Onboarding initial</span>
                          <span className="text-[10px] text-muted-foreground">(prise en main 30min)</span>
                        </div>
                        <Badge className="bg-emerald-100 text-emerald-700 text-[10px] font-semibold" data-testid="onboarding-offert">OFFERT</Badge>
                      </div>
                      <div className="flex items-center justify-between text-xs">
                        <div className="flex items-center gap-2">
                          <Clock className="w-3.5 h-3.5 text-blue-500" />
                          <span>Reunions supplementaires</span>
                        </div>
                        <span className="text-blue-700 font-semibold text-[11px]" data-testid="prix-reunion-supp">{ONBOARDING.reunion_supp} EUR/h</span>
                      </div>
                      <div className="flex items-center justify-between text-xs opacity-60">
                        <div className="flex items-center gap-2">
                          <Users className="w-3.5 h-3.5 text-gray-400" />
                          <span>Journee formation collective</span>
                        </div>
                        <Badge variant="outline" className="text-[9px] border-gray-300 text-gray-500" data-testid="formation-badge">Prochainement</Badge>
                      </div>
                    </div>

                    {/* Parrainage */}
                    <div className="space-y-2 bg-amber-50/50 rounded-lg p-2.5 border border-amber-100" data-testid="parrainage-section">
                      <p className="text-xs font-semibold text-amber-700 flex items-center gap-1"><Handshake className="w-3.5 h-3.5" />Parrainage client</p>
                      <p className="text-[10px] text-amber-600">Le parrain recoit 1 mois gratuit pour chaque filleul qui souscrit.</p>
                      <Input
                        placeholder="Code parrainage (si le prospect a un parrain)"
                        className="text-xs h-8"
                        value={prospect.code_parrainage}
                        onChange={e => setProspect(p => ({ ...p, code_parrainage: e.target.value }))}
                        data-testid="input-code-parrainage"
                      />
                      {prospect.code_parrainage && (
                        <p className="text-[10px] text-emerald-600 flex items-center gap-1"><CheckCircle2 className="w-3 h-3" />Code parrainage renseigne — le parrain sera credite</p>
                      )}
                    </div>
                  </div>
                </TabsContent>

                <TabsContent value="transcript" className="flex-1 overflow-auto px-4 pb-4 mt-2">
                  <div className="bg-gray-50 rounded-lg p-3 min-h-[200px]" data-testid="transcript-panel">
                    {liveTranscript ? (
                      <p className="text-sm text-gray-700 whitespace-pre-wrap leading-relaxed">{liveTranscript}</p>
                    ) : (
                      <div className="text-center text-muted-foreground py-12">
                        <Mic className="w-8 h-8 mx-auto mb-2 opacity-30" />
                        <p className="text-sm">La transcription apparaitra ici en temps reel</p>
                        <p className="text-xs">Commencez a parler...</p>
                      </div>
                    )}
                  </div>
                </TabsContent>

                <TabsContent value="closing" className="flex-1 overflow-auto px-4 pb-4 mt-2">
                  <div className="space-y-3">
                    <p className="text-xs text-muted-foreground">Envoyez le devis et l'acces demo directement pendant la visio — pas besoin de couper l'appel.</p>

                    <Button className="w-full bg-emerald-600 hover:bg-emerald-700" onClick={genererDevis} data-testid="btn-generer-devis">
                      <FileText className="w-4 h-4 mr-2" />
                      {prospect.facturation === 'annuel'
                        ? `Devis annuel (${calcPrixAnnuel(calcPrixTotal(prospect.modules_suggeres, prospect.nb_workspaces, prospect.nb_collaborateurs, prospect.agents_ia_selectionnes))} EUR/an)`
                        : `Devis mensuel (${calcPrixTotal(prospect.modules_suggeres, prospect.nb_workspaces, prospect.nb_collaborateurs, prospect.agents_ia_selectionnes)} EUR/mois)`
                      }
                    </Button>
                    {devisGenere && (
                      <div className="p-3 bg-green-50 rounded-lg text-xs border border-green-200 space-y-1">
                        <p className="font-semibold text-green-700 text-sm">
                          {prospect.facturation === 'annuel'
                            ? `Devis : ${calcPrixAnnuel(devisGenere.montant_mensuel)} EUR/an (1 mois offert)`
                            : `Devis : ${devisGenere.montant_mensuel} EUR/mois`
                          }
                        </p>
                        <p className="text-green-600">Base {PACK_BASE_PRIX} EUR + {prospect.modules_suggeres.filter(id => !MODULES_DISPO.find(m => m.id === id)?.base).length} modules payants</p>
                        <p className="text-green-600">{prospect.nb_workspaces} workspace(s), {prospect.nb_collaborateurs} collab(s), {prospect.nb_agents_ia} agent(s) IA</p>
                        <p className="text-green-600">Onboarding 30min OFFERT — Reunions supp. {ONBOARDING.reunion_supp} EUR/h</p>
                        {prospect.code_parrainage && <p className="text-amber-600 font-medium">Parrainage : {prospect.code_parrainage} — le parrain recoit 1 mois gratuit</p>}
                      </div>
                    )}

                    <Button className="w-full" variant="outline" onClick={genererAccesDemo} data-testid="btn-acces-demo">
                      <Eye className="w-4 h-4 mr-2" />Creer acces demo (3j)
                    </Button>
                    {accesDemo && (
                      <div className="p-2.5 bg-blue-50 rounded-lg text-xs border border-blue-200 space-y-1">
                        <p className="font-semibold text-blue-700">Acces demo cree</p>
                        <p>Email: {accesDemo.email}</p>
                        <p>Mot de passe: {accesDemo.password}</p>
                        <p>Expire: {accesDemo.expire_date}</p>
                      </div>
                    )}

                    <Button className="w-full" variant="outline" onClick={() => setShowOnboardingDialog(true)}>
                      <Calendar className="w-4 h-4 mr-2" />Planifier onboarding (J+7)
                    </Button>

                    {selectedPartenaire && (
                      <Button className="w-full bg-amber-500 hover:bg-amber-600 text-white" onClick={() => envoyerRapportPartenaire(selectedPartenaire.id)} data-testid="btn-envoyer-rapport-partenaire">
                        <Handshake className="w-4 h-4 mr-2" />Rapport a {selectedPartenaire.nom}
                      </Button>
                    )}

                    {documentsRequis.length > 0 && (
                      <div className="border rounded-lg p-3">
                        <p className="text-xs font-semibold mb-2">Documents a demander au prospect :</p>
                        {documentsRequis.map(doc => (
                          <div key={doc.id} className="flex items-center gap-2 text-xs py-0.5">
                            <CheckCircle className={`w-3.5 h-3.5 ${doc.requis ? 'text-red-500' : 'text-gray-300'}`} />
                            <span>{doc.label}</span>
                            {doc.requis && <Badge className="text-[9px] bg-red-100 text-red-600">requis</Badge>}
                          </div>
                        ))}
                      </div>
                    )}

                    {/* Compte-rendu post-visio */}
                    {(compteRendu || loadingCR) && (
                      <div className="border-2 border-purple-200 rounded-lg p-3 bg-purple-50/50" data-testid="compte-rendu-section">
                        <p className="text-xs font-bold text-purple-700 mb-2 flex items-center gap-1"><FileText className="w-3.5 h-3.5" />Compte-rendu post-visio</p>
                        {loadingCR ? (
                          <div className="flex items-center gap-2 text-xs text-purple-500">
                            <Loader2 className="w-3.5 h-3.5 animate-spin" />
                            <span>Generation du compte-rendu en cours...</span>
                          </div>
                        ) : compteRendu ? (
                          <div className="space-y-2 text-xs">
                            {compteRendu.resume && (
                              <div><p className="font-semibold text-purple-800">Resume</p><p className="text-gray-700">{compteRendu.resume}</p></div>
                            )}
                            {compteRendu.modules_proposes?.length > 0 && (
                              <div><p className="font-semibold text-purple-800">Modules proposes</p><p className="text-gray-700">{compteRendu.modules_proposes.join(', ')}</p></div>
                            )}
                            {compteRendu.objections?.length > 0 && (
                              <div><p className="font-semibold text-red-700">Objections detectees</p><p className="text-gray-700">{compteRendu.objections.join(' | ')}</p></div>
                            )}
                            {compteRendu.actions_faites?.length > 0 && (
                              <div><p className="font-semibold text-emerald-700">Actions appliquees</p><p className="text-gray-700">{compteRendu.actions_faites.join(' | ')}</p></div>
                            )}
                            {compteRendu.prochaine_etape && (
                              <div className="bg-amber-50 p-2 rounded border border-amber-200">
                                <p className="font-semibold text-amber-800">Prochaine etape</p>
                                <p className="text-amber-700">{compteRendu.prochaine_etape}</p>
                              </div>
                            )}
                            {compteRendu.note_amelioration && (
                              <div className="bg-blue-50 p-2 rounded border border-blue-200">
                                <p className="font-semibold text-blue-800">Pour progresser</p>
                                <p className="text-blue-700">{compteRendu.note_amelioration}</p>
                              </div>
                            )}
                          </div>
                        ) : null}
                      </div>
                    )}
                    {!compteRendu && !loadingCR && !isRecording && transcriptBufferRef.current && (
                      <Button className="w-full" variant="outline" onClick={generateCompteRendu} data-testid="btn-generer-cr">
                        <FileText className="w-4 h-4 mr-2" />Generer le compte-rendu
                      </Button>
                    )}
                  </div>
                </TabsContent>
              </Tabs>
            </Card>
          </div>

          {/* COL DROITE (4/12) : Coach IA + Questions de vente */}
          <div className="col-span-4 flex flex-col min-h-0">
            <Card className={`border-2 flex-1 flex flex-col min-h-0 ${isRecording ? 'border-amber-300 bg-gradient-to-b from-amber-50/50 to-white' : 'border-emerald-200'}`} data-testid="coaching-sidebar">
              <Tabs defaultValue="questions" className="flex-1 flex flex-col min-h-0">
                <TabsList className="mx-3 mt-3 shrink-0">
                  <TabsTrigger value="questions" className="text-xs"><ListChecks className="w-3.5 h-3.5 mr-1" />Script vente</TabsTrigger>
                  <TabsTrigger value="coach" className="text-xs"><Sparkles className="w-3.5 h-3.5 mr-1" />Guide proactif</TabsTrigger>
                </TabsList>

                <TabsContent value="questions" className="flex-1 overflow-auto min-h-0 mt-1">
                  <ScrollArea className="h-[calc(100vh-250px)] px-3 pb-3">
                    <div className="space-y-4">
                      {SALES_SCRIPT.map(section => (
                        <div key={section.phase}>
                          <p className={`text-xs font-bold uppercase tracking-wide mb-2 text-${section.color}-600`}>
                            {section.phase}
                          </p>
                          <div className="space-y-1.5">
                            {section.questions.map(q => (
                              <div key={q.id}
                                className={`p-2 rounded-lg cursor-pointer transition-all text-xs ${
                                  answeredQuestions[q.id]
                                    ? 'bg-emerald-50 border border-emerald-200'
                                    : 'bg-white border border-gray-100 hover:border-gray-300 hover:shadow-sm'
                                }`}
                                onClick={() => setAnsweredQuestions(prev => ({ ...prev, [q.id]: !prev[q.id] }))}
                                data-testid={`question-${q.id}`}
                              >
                                <div className="flex items-start gap-2">
                                  {answeredQuestions[q.id]
                                    ? <CheckCircle2 className="w-4 h-4 text-emerald-600 shrink-0 mt-0.5" />
                                    : <CircleDot className={`w-4 h-4 text-${section.color}-400 shrink-0 mt-0.5`} />
                                  }
                                  <div className="min-w-0">
                                    <p className={`font-medium ${answeredQuestions[q.id] ? 'line-through text-emerald-700 opacity-70' : ''}`}>
                                      {q.text}
                                    </p>
                                    <p className="text-[10px] text-muted-foreground mt-0.5 flex items-center gap-1">
                                      <HelpCircle className="w-3 h-3" />{q.tip}
                                    </p>
                                  </div>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      ))}
                    </div>
                  </ScrollArea>
                </TabsContent>

                <TabsContent value="coach" className="flex-1 overflow-auto min-h-0 mt-1">
                  <ScrollArea className="h-[calc(100vh-250px)] px-3 pb-3">
                    {isRecording && coachingTips.length === 0 ? (
                      <div className="text-center py-12 text-muted-foreground">
                        <Brain className="w-8 h-8 mx-auto mb-2 animate-pulse text-amber-400" />
                        <p className="text-xs font-medium">Le guide analyse la conversation...</p>
                        <p className="text-[11px] mt-1">Instructions dans quelques secondes</p>
                      </div>
                    ) : coachingTips.length === 0 && !isRecording ? (
                      <div className="text-center py-12 text-muted-foreground">
                        <Sparkles className="w-8 h-8 mx-auto mb-2 opacity-30" />
                        <p className="text-xs font-medium">Guide proactif</p>
                        <p className="text-[11px]">Le guide vous dira exactement quoi faire</p>
                        <p className="text-[11px]">et quel module proposer pendant la visio</p>
                      </div>
                    ) : (
                      <div className="space-y-2.5">
                        {coachingTips.map((tip, i) => {
                          const isDone = feedbackSent[i] === 'done';
                          const isSkipped = feedbackSent[i] === 'skipped';
                          const typeConfig = {
                            action: { bg: 'bg-blue-50 border-blue-300', icon: <ArrowRight className="w-3.5 h-3.5 text-blue-600" />, label: 'A FAIRE' },
                            argument_produit: { bg: 'bg-emerald-50 border-emerald-300', icon: <Euro className="w-3.5 h-3.5 text-emerald-600" />, label: 'MODULE A PROPOSER' },
                            alerte: { bg: 'bg-red-50 border-red-300', icon: <AlertTriangle className="w-3.5 h-3.5 text-red-600" />, label: 'ATTENTION' },
                            lecon_passee: { bg: 'bg-amber-50 border-amber-300', icon: <Activity className="w-3.5 h-3.5 text-amber-600" />, label: 'LECON VENTE PRECEDENTE' },
                            invite_temoin: { bg: 'bg-orange-50 border-orange-300', icon: <Users className="w-3.5 h-3.5 text-orange-600" />, label: 'INVITER TEMOIN' },
                          };
                          const cfg = typeConfig[tip.type] || { bg: 'bg-purple-50 border-purple-300', icon: <Sparkles className="w-3.5 h-3.5 text-purple-600" />, label: 'CONSEIL' };

                          return (
                            <div key={i} className={`p-3 rounded-lg text-xs border-l-4 ${cfg.bg} ${isDone ? 'opacity-50' : ''} ${isSkipped ? 'opacity-30' : ''}`} data-testid={`coaching-tip-${i}`}>
                              <div className="flex items-center gap-1.5 mb-1.5">
                                {cfg.icon}
                                <span className="font-bold text-[10px] uppercase tracking-wider">{cfg.label}</span>
                                {tip.urgence === 'haute' && <Badge variant="destructive" className="text-[9px] px-1 py-0 ml-auto">URGENT</Badge>}
                                {tip.urgence === 'basse' && <Badge variant="outline" className="text-[9px] px-1 py-0 ml-auto">info</Badge>}
                              </div>
                              <p className="leading-relaxed">{tip.message || tip.conseil || tip.texte}</p>
                              {!feedbackSent[i] && (
                                <div className="flex items-center gap-2 mt-2 pt-2 border-t border-dashed border-gray-200">
                                  <button
                                    className="flex items-center gap-1 px-2 py-1 rounded bg-emerald-100 hover:bg-emerald-200 text-emerald-700 text-[10px] font-medium transition-colors"
                                    onClick={() => markTipAction(i, 'done')}
                                    data-testid={`tip-done-${i}`}
                                  >
                                    <CheckCircle2 className="w-3 h-3" /> Fait
                                  </button>
                                  <button
                                    className="flex items-center gap-1 px-2 py-1 rounded bg-gray-100 hover:bg-gray-200 text-gray-500 text-[10px] font-medium transition-colors"
                                    onClick={() => markTipAction(i, 'skipped')}
                                    data-testid={`tip-skip-${i}`}
                                  >
                                    <XCircle className="w-3 h-3" /> Pas pertinent
                                  </button>
                                </div>
                              )}
                              {isDone && <p className="text-[10px] text-emerald-600 mt-1 flex items-center gap-1"><CheckCircle2 className="w-3 h-3" /> Applique</p>}
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </ScrollArea>
                </TabsContent>
              </Tabs>
            </Card>
          </div>
        </div>
      )}

      {/* ===== PARTENAIRES ===== */}
      {phase === 'partenaires' && (
        <PartenairesView
          partenaires={partenaires} commissionsRapport={commissionsRapport}
          loadingCommissions={loadingCommissions}
          onAdd={() => { setEditPartenaire(null); setPartenaireForm({ nom: '', entreprise: '', email: '', telephone: '', type_partenaire: 'apporteur', commission_pct: 10, duree_commission_mois: 12, notes: '' }); setShowPartenaireDialog(true); }}
          onEdit={openEditPartenaire} onDelete={deletePartenaire}
          onRefreshCommissions={loadCommissions}
          onBack={() => { setPhase('dashboard'); loadDashboard(); }}
        />
      )}

      {/* Dialog onboarding */}
      <Dialog open={showOnboardingDialog} onOpenChange={setShowOnboardingDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Planifier le 1er Onboarding</DialogTitle>
            <DialogDescription>Une semaine apres la signature</DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <Input type="date" defaultValue={new Date(Date.now() + 7 * 86400000).toISOString().split('T')[0]} />
            <Input type="time" defaultValue="10:00" />
            <Textarea placeholder="Notes..." rows={2} />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowOnboardingDialog(false)}>Annuler</Button>
            <Button className="bg-emerald-600 hover:bg-emerald-700" onClick={() => { toast.success('Onboarding planifie !'); setShowOnboardingDialog(false); }}>Planifier</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog Partenaire */}
      <Dialog open={showPartenaireDialog} onOpenChange={setShowPartenaireDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle data-testid="partenaire-dialog-title">{editPartenaire ? 'Modifier le partenaire' : 'Nouveau partenaire'}</DialogTitle>
            <DialogDescription>Apporteur d'affaires, revendeur ou prescripteur</DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <Input placeholder="Nom" value={partenaireForm.nom} onChange={e => setPartenaireForm(f => ({ ...f, nom: e.target.value }))} data-testid="partenaire-nom" />
            <Input placeholder="Entreprise" value={partenaireForm.entreprise} onChange={e => setPartenaireForm(f => ({ ...f, entreprise: e.target.value }))} data-testid="partenaire-entreprise" />
            <Input placeholder="Email" type="email" value={partenaireForm.email} onChange={e => setPartenaireForm(f => ({ ...f, email: e.target.value }))} data-testid="partenaire-email" />
            <Input placeholder="Telephone" value={partenaireForm.telephone} onChange={e => setPartenaireForm(f => ({ ...f, telephone: e.target.value }))} data-testid="partenaire-telephone" />
            <Select value={partenaireForm.type_partenaire} onValueChange={v => setPartenaireForm(f => ({ ...f, type_partenaire: v }))}>
              <SelectTrigger data-testid="partenaire-type"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="apporteur">Apporteur d'affaires</SelectItem>
                <SelectItem value="revendeur">Revendeur</SelectItem>
                <SelectItem value="prescripteur">Prescripteur</SelectItem>
              </SelectContent>
            </Select>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className="text-xs text-muted-foreground">Commission (%)</label>
                <Input type="number" min={0} max={50} value={partenaireForm.commission_pct} onChange={e => setPartenaireForm(f => ({ ...f, commission_pct: parseFloat(e.target.value) || 0 }))} data-testid="partenaire-commission" />
              </div>
              <div>
                <label className="text-xs text-muted-foreground">Duree (mois)</label>
                <Input type="number" min={1} max={36} value={partenaireForm.duree_commission_mois} onChange={e => setPartenaireForm(f => ({ ...f, duree_commission_mois: parseInt(e.target.value) || 12 }))} data-testid="partenaire-duree" />
              </div>
            </div>
            <Textarea placeholder="Notes..." value={partenaireForm.notes} onChange={e => setPartenaireForm(f => ({ ...f, notes: e.target.value }))} rows={2} data-testid="partenaire-notes" />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowPartenaireDialog(false)}>Annuler</Button>
            <Button className="bg-emerald-600 hover:bg-emerald-700" onClick={savePartenaire} data-testid="partenaire-save-btn">
              {editPartenaire ? 'Modifier' : 'Creer'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ============================
// DASHBOARD
// ============================
function DashboardView({ todayRdvs, nextRdv, rdvList, pipeline, hotProspects, partenaires, suiviData, loadingSuivi, onStartVisio, onRefresh, onGoPartenaires, onLoadSuivi }) {
  return (
    <div className="space-y-4" data-testid="dashboard-view">
      {nextRdv ? (
        <Card className="border-2 border-emerald-300 bg-gradient-to-r from-emerald-50 to-white" data-testid="next-rdv-card">
          <CardContent className="p-5">
            <div className="flex items-center justify-between">
              <div className="space-y-1">
                <p className="text-xs text-emerald-600 font-semibold uppercase tracking-wide">Prochain rendez-vous</p>
                <h2 className="text-xl font-bold">{nextRdv.nom}</h2>
                <div className="flex items-center gap-4 text-sm text-muted-foreground">
                  <span className="flex items-center gap-1"><Calendar className="w-3.5 h-3.5" />{new Date(nextRdv.date + 'T00:00').toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long' })}</span>
                  <span className="flex items-center gap-1"><Clock className="w-3.5 h-3.5" />{nextRdv.heure}</span>
                  <Badge variant="outline" className="capitalize">{nextRdv.canal || 'visio'}</Badge>
                </div>
                {nextRdv.entreprise && <p className="text-sm"><Building2 className="w-3.5 h-3.5 inline mr-1" />{nextRdv.entreprise}</p>}
                {nextRdv.message && <p className="text-xs text-muted-foreground italic mt-1">"{nextRdv.message}"</p>}
              </div>
              <Button size="lg" className="bg-emerald-600 hover:bg-emerald-700 h-14 px-6 text-base" onClick={() => onStartVisio(nextRdv)} data-testid="btn-start-visio">
                <Video className="w-5 h-5 mr-2" />Demarrer la visio
              </Button>
            </div>
          </CardContent>
        </Card>
      ) : (
        <Card className="border-dashed border-2">
          <CardContent className="p-8 text-center">
            <Calendar className="w-10 h-10 mx-auto text-muted-foreground/30 mb-2" />
            <p className="font-medium">Aucun RDV a venir</p>
            <p className="text-sm text-muted-foreground">Les RDV pris via votre lien de booking apparaitront ici</p>
          </CardContent>
        </Card>
      )}

      <div className="grid grid-cols-5 gap-3">
        <MetricCard icon={Calendar} label="RDV aujourd'hui" value={todayRdvs.length} color="emerald" />
        <MetricCard icon={Users} label="Prospects actifs" value={pipeline.length} color="blue" />
        <MetricCard icon={TrendingUp} label="Prospects chauds" value={hotProspects.length} color="amber" />
        <MetricCard icon={Target} label="A closer" value={pipeline.filter(p => p.statut === 'qualifie').length} color="purple" />
        <div onClick={onGoPartenaires} className="cursor-pointer">
          <MetricCard icon={Handshake} label="Partenaires" value={partenaires.length} color="emerald" />
        </div>
      </div>

      {todayRdvs.length > 0 && (
        <div className="space-y-2">
          <h3 className="font-semibold text-sm text-muted-foreground uppercase tracking-wide">Aujourd'hui</h3>
          {todayRdvs.map(rdv => (
            <Card key={rdv.id} className="hover:shadow-md transition-shadow cursor-pointer" onClick={() => onStartVisio(rdv)} data-testid={`rdv-today-${rdv.id}`}>
              <CardContent className="p-3 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-emerald-100 flex items-center justify-center"><Video className="w-5 h-5 text-emerald-600" /></div>
                  <div>
                    <p className="font-medium">{rdv.nom}</p>
                    <p className="text-xs text-muted-foreground">{rdv.heure} — {rdv.entreprise || rdv.email}</p>
                  </div>
                </div>
                <ChevronRight className="w-5 h-5 text-muted-foreground" />
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {pipeline.length > 0 && (
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <h3 className="font-semibold text-sm text-muted-foreground uppercase tracking-wide">Pipeline commercial</h3>
            <Button variant="ghost" size="sm" onClick={onRefresh}><Loader2 className="w-3.5 h-3.5 mr-1" />Actualiser</Button>
          </div>
          <div className="grid grid-cols-2 gap-2">
            {pipeline.slice(0, 6).map(p => (
              <Card key={p.id} className="hover:shadow-sm transition-shadow" data-testid={`pipeline-card-${p.id}`}>
                <CardContent className="p-3">
                  <div className="flex items-center justify-between">
                    <div className="min-w-0 flex-1">
                      <p className="font-medium text-sm truncate">{p.nom || p.entreprise || 'Prospect'} {p.prenom || ''}</p>
                      <p className="text-xs text-muted-foreground truncate">{p.entreprise || p.email || ''}</p>
                    </div>
                    <Badge className={`ml-2 shrink-0 ${p.statut === 'qualifie' ? 'bg-green-100 text-green-700' : p.statut === 'gagne' ? 'bg-emerald-100 text-emerald-700' : 'bg-gray-100 text-gray-700'}`}>
                      {p.statut || 'nouveau'}
                    </Badge>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      )}

      {/* Suivi post-RDV */}
      <Card data-testid="suivi-post-rdv">
        <CardHeader className="pb-2">
          <div className="flex items-center justify-between">
            <CardTitle className="text-sm flex items-center gap-2">
              <Activity className="w-4 h-4 text-blue-600" />Suivi post-RDV
            </CardTitle>
            <Button variant="ghost" size="sm" onClick={onLoadSuivi} data-testid="btn-load-suivi">
              {loadingSuivi ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
              <span className="ml-1">Actualiser</span>
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {loadingSuivi ? (
            <div className="text-center py-4"><Loader2 className="w-5 h-5 animate-spin mx-auto text-blue-600" /></div>
          ) : suiviData ? (
            <div className="space-y-3">
              <div className="grid grid-cols-4 gap-2">
                <div className="bg-blue-50 rounded-lg p-2.5 text-center">
                  <p className="text-lg font-bold text-blue-700" data-testid="stat-demos-actives">{suiviData.stats?.demos_actives || 0}</p>
                  <p className="text-[10px] text-blue-600">Demos actives</p>
                </div>
                <div className="bg-green-50 rounded-lg p-2.5 text-center">
                  <p className="text-lg font-bold text-green-700" data-testid="stat-demos-connectees">{suiviData.stats?.demos_connectees || 0}</p>
                  <p className="text-[10px] text-green-600">Connectes</p>
                </div>
                <div className="bg-amber-50 rounded-lg p-2.5 text-center">
                  <p className="text-lg font-bold text-amber-700" data-testid="stat-emails-ouverts">{suiviData.stats?.emails_ouverts || 0}</p>
                  <p className="text-[10px] text-amber-600">Emails ouverts</p>
                </div>
                <div className="bg-red-50 rounded-lg p-2.5 text-center">
                  <p className="text-lg font-bold text-red-700" data-testid="stat-relances">{suiviData.stats?.relances_a_faire || 0}</p>
                  <p className="text-[10px] text-red-600">A relancer</p>
                </div>
              </div>
              {suiviData.demos?.length > 0 && (
                <div className="space-y-1">
                  <p className="text-xs font-semibold text-muted-foreground">Acces demo</p>
                  {suiviData.demos.slice(0, 5).map(d => (
                    <div key={d.id} className={`flex items-center justify-between text-xs p-2 rounded ${d.est_expire ? 'bg-gray-50 opacity-60' : d.nb_connexions > 0 ? 'bg-green-50' : 'bg-blue-50'}`}>
                      <div className="flex items-center gap-2 min-w-0">
                        <Eye className="w-3.5 h-3.5 shrink-0" />
                        <span className="font-medium truncate">{d.nom || d.email}</span>
                        {d.entreprise && <span className="text-muted-foreground truncate">({d.entreprise})</span>}
                      </div>
                      <div className="flex items-center gap-2 shrink-0">
                        {d.nb_connexions > 0 ? (
                          <Badge className="bg-green-100 text-green-700 text-[10px]">{d.nb_connexions} connexion(s)</Badge>
                        ) : d.est_expire ? (
                          <Badge variant="outline" className="text-[10px] text-gray-400">Expire</Badge>
                        ) : (
                          <Badge variant="outline" className="text-[10px]">Pas encore connecte</Badge>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
              {suiviData.prospects_a_relancer?.length > 0 && (
                <div className="space-y-1">
                  <p className="text-xs font-semibold text-red-600">Prospects a relancer (inactifs 7j+)</p>
                  {suiviData.prospects_a_relancer.slice(0, 5).map(p => (
                    <div key={p.id} className="flex items-center justify-between text-xs p-2 rounded bg-red-50">
                      <span className="font-medium">{p.nom || p.contact_nom || p.nom_entreprise || p.email || 'Prospect'}</span>
                      <Badge className="bg-red-100 text-red-700 text-[10px]">{p.statut}</Badge>
                    </div>
                  ))}
                </div>
              )}
            </div>
          ) : (
            <p className="text-sm text-muted-foreground text-center py-3">Cliquez sur Actualiser pour voir le suivi de vos prospects</p>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function MetricCard({ icon: Icon, label, value, color }) {
  const colors = { emerald: 'bg-emerald-50 border-emerald-200 text-emerald-700', blue: 'bg-blue-50 border-blue-200 text-blue-700', amber: 'bg-amber-50 border-amber-200 text-amber-700', purple: 'bg-purple-50 border-purple-200 text-purple-700' };
  return (
    <Card className={colors[color]}>
      <CardContent className="p-3 flex items-center gap-3">
        <Icon className="w-5 h-5" />
        <div><p className="text-xl font-bold">{value}</p><p className="text-xs opacity-70">{label}</p></div>
      </CardContent>
    </Card>
  );
}

// ============================
// PARTENAIRES
// ============================
function PartenairesView({ partenaires, commissionsRapport, loadingCommissions, onAdd, onEdit, onDelete, onRefreshCommissions, onBack }) {
  const TYPE_LABELS = { apporteur: "Apporteur d'affaires", revendeur: 'Revendeur', prescripteur: 'Prescripteur' };
  const TYPE_COLORS = { apporteur: 'bg-blue-100 text-blue-700', revendeur: 'bg-purple-100 text-purple-700', prescripteur: 'bg-amber-100 text-amber-700' };
  return (
    <div className="space-y-4" data-testid="partenaires-view">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold flex items-center gap-2"><Handshake className="w-5 h-5 text-emerald-600" />Canaux de vente & Partenaires</h2>
          <p className="text-sm text-muted-foreground">Gerez vos apporteurs d'affaires, revendeurs et prescripteurs</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={onBack} data-testid="btn-back-dashboard">Retour</Button>
          <Button className="bg-emerald-600 hover:bg-emerald-700" onClick={onAdd} data-testid="btn-add-partenaire"><PlusCircle className="w-4 h-4 mr-2" />Nouveau partenaire</Button>
        </div>
      </div>
      {partenaires.length === 0 ? (
        <Card className="border-dashed border-2"><CardContent className="p-8 text-center"><Handshake className="w-10 h-10 mx-auto text-muted-foreground/30 mb-2" /><p className="font-medium">Aucun partenaire</p></CardContent></Card>
      ) : (
        <div className="grid grid-cols-2 gap-3">
          {partenaires.map(p => (
            <Card key={p.id} className={`hover:shadow-md transition-shadow ${p.actif === false ? 'opacity-50' : ''}`} data-testid={`partenaire-card-${p.id}`}>
              <CardContent className="p-4">
                <div className="flex items-start justify-between">
                  <div className="space-y-1 min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <p className="font-semibold truncate">{p.nom}</p>
                      <Badge className={`text-xs ${TYPE_COLORS[p.type_partenaire] || 'bg-gray-100 text-gray-700'}`}>{TYPE_LABELS[p.type_partenaire] || p.type_partenaire}</Badge>
                    </div>
                    {p.entreprise && <p className="text-sm text-muted-foreground flex items-center gap-1"><Building2 className="w-3 h-3" />{p.entreprise}</p>}
                    <p className="text-sm text-muted-foreground flex items-center gap-1"><Mail className="w-3 h-3" />{p.email}</p>
                    <div className="flex items-center gap-3 mt-2">
                      <Badge variant="outline" className="text-xs"><Percent className="w-3 h-3 mr-1" />{p.commission_pct}% sur {p.duree_commission_mois} mois</Badge>
                      <Badge variant="outline" className="text-xs"><Users className="w-3 h-3 mr-1" />{p.nb_prospects_apportes || 0} prospect(s)</Badge>
                    </div>
                    {p.access_token && (
                      <div className="mt-2 flex items-center gap-2">
                        <code className="text-[11px] bg-gray-100 px-2 py-0.5 rounded truncate max-w-[200px]">{`${window.location.origin}/partenaire/${p.access_token}`}</code>
                        <Button variant="ghost" size="icon" className="h-6 w-6" onClick={() => { navigator.clipboard.writeText(`${window.location.origin}/partenaire/${p.access_token}`); toast.success('Lien copie !'); }} data-testid={`btn-copy-link-${p.id}`}><Copy className="w-3 h-3" /></Button>
                        <Button variant="ghost" size="icon" className="h-6 w-6" onClick={() => window.open(`/partenaire/${p.access_token}`, '_blank')} data-testid={`btn-open-link-${p.id}`}><ExternalLink className="w-3 h-3" /></Button>
                      </div>
                    )}
                  </div>
                  <div className="flex gap-1 ml-2 shrink-0">
                    <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => onEdit(p)} data-testid={`btn-edit-partenaire-${p.id}`}><Pencil className="w-3.5 h-3.5" /></Button>
                    <Button variant="ghost" size="icon" className="h-8 w-8 text-red-500 hover:text-red-700" onClick={() => onDelete(p.id)} data-testid={`btn-delete-partenaire-${p.id}`}><Trash2 className="w-3.5 h-3.5" /></Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
      <Card data-testid="commissions-rapport">
        <CardHeader className="pb-2">
          <div className="flex items-center justify-between">
            <CardTitle className="text-sm flex items-center gap-2"><Euro className="w-4 h-4 text-emerald-600" />Commissions du mois</CardTitle>
            <Button variant="ghost" size="sm" onClick={onRefreshCommissions} data-testid="btn-refresh-commissions">
              {loadingCommissions ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <BarChart3 className="w-3.5 h-3.5" />} Actualiser
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {loadingCommissions ? (
            <div className="text-center py-4"><Loader2 className="w-6 h-6 animate-spin mx-auto text-emerald-600" /></div>
          ) : commissionsRapport ? (
            <div className="space-y-3">
              <div className="flex items-center justify-between p-3 bg-emerald-50 rounded-lg">
                <span className="text-sm font-medium">Total commissions dues</span>
                <span className="text-xl font-bold text-emerald-700" data-testid="total-commissions">{commissionsRapport.total_commissions?.toFixed(2) || '0.00'} EUR/mois</span>
              </div>
              {commissionsRapport.rapport?.map(r => (
                <div key={r.partenaire_id} className="border rounded-lg p-3 space-y-2" data-testid={`commission-row-${r.partenaire_id}`}>
                  <div className="flex items-center justify-between">
                    <div><p className="font-medium text-sm">{r.partenaire_nom}</p><p className="text-xs text-muted-foreground">{r.type} — {r.commission_pct}%</p></div>
                    <span className="font-bold text-emerald-700">{r.commission_mois?.toFixed(2)} EUR</span>
                  </div>
                  {r.details?.length > 0 && (
                    <div className="space-y-1 pl-3 border-l-2 border-emerald-200">
                      {r.details.map((d, i) => (
                        <div key={i} className="flex justify-between text-xs text-muted-foreground">
                          <span>{d.nom} ({d.statut})</span>
                          <span>{d.prix_mensuel} EUR x {r.commission_pct}% = {d.commission} EUR</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ))}
              {(!commissionsRapport.rapport || commissionsRapport.rapport.length === 0) && (
                <p className="text-sm text-muted-foreground text-center py-2">Aucune commission ce mois-ci</p>
              )}
            </div>
          ) : (
            <p className="text-sm text-muted-foreground text-center py-4">Cliquez sur Actualiser pour charger les commissions</p>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
